import {
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TruckTableRow } from './truck-table-row'

import type { SortState } from '@/components/shared/sortable-table-head'
import type { VehicleWithTruck } from '@repo/shared'

import { ListPagination } from '@/components/shared/list-pagination'
import {
  SortableTableHead,
  sortItems,
  toggleSort,
} from '@/components/shared/sortable-table-head'
import { useTrucksPaginated } from '@/features/vehicles/hooks/use-trucks-paginated'

type TruckListProps = {
  search?: string
}

const truckAccessor = (item: VehicleWithTruck, key: string) => {
  switch (key) {
    case 'licensePlate':
      return item.licensePlate
    case 'manufacturer':
      return item.manufacturer
    case 'model':
      return item.truck?.vehicleModel
    case 'status':
      return item.status
    case 'trailer':
      return item.assignedTrailer?.licensePlate
    default:
      return null
  }
}

export function TruckList({ search }: TruckListProps) {
  const { t } = useTranslation('vehicles')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<SortState>(null)

  const { data: trucksData, isLoading } = useTrucksPaginated({
    page,
    pageSize,
    search: search || undefined,
  })

  useEffect(() => {
    setPage(1)
  }, [search])

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setPage(1)
  }

  const handleSort = (key: string) => setSort((prev) => toggleSort(prev, key))

  const sortedTrucks = useMemo(
    () => sortItems(trucksData?.items ?? [], sort, truckAccessor),
    [trucksData?.items, sort],
  )

  return (
    <>
      {isLoading ? (
        <div className="overflow-hidden rounded-lg border">
          <Skeleton className="h-10 w-full rounded-none" />
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-none border-t" />
          ))}
        </div>
      ) : trucksData?.items && trucksData.items.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <SortableTableHead
                    sortKey="licensePlate"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.vehicle')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="manufacturer"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.manufacturer')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="model"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.model')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="status"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.status')}
                  </SortableTableHead>
                  <SortableTableHead
                    sortKey="trailer"
                    sort={sort}
                    onSort={handleSort}
                  >
                    {t('table.trailer')}
                  </SortableTableHead>
                  <TableHead>{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTrucks.map((vehicle) => (
                  <TruckTableRow key={vehicle.id} vehicle={vehicle} />
                ))}
              </TableBody>
            </Table>
          </div>

          <ListPagination
            page={trucksData.page}
            totalPages={trucksData.totalPages}
            pageSize={trucksData.pageSize}
            totalCount={trucksData.totalCount}
            hasPrevious={trucksData.hasPrevious}
            hasNext={trucksData.hasNext}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            className="mt-4 sm:px-4"
          />
        </>
      ) : (
        <div className="py-12 text-center">
          <p className="text-muted-foreground text-sm">
            {t('noVehiclesFound')}
          </p>
        </div>
      )}
    </>
  )
}
